import type { DocV1 } from "./types";

export type StoredTemplate = {
  id: string;
  name: string;
  sizeMM: { w: number; h: number };
  createdAt: string;
  updatedAt: string;
};

const API_BASE = "/api";

function getCookie(name: string): string | null {
  const m = document.cookie.match(new RegExp(`(?:^|; )${name.replace(/[-[\]/{}()*+?.\\^$|]/g, "\\$&")}=([^;]*)`));
  return m ? decodeURIComponent(m[1]) : null;
}

function ensureUid(): string {
  const existing = getCookie("labler_uid");
  if (existing) return existing;
  const uid = (crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`).toString();
  document.cookie = `labler_uid=${encodeURIComponent(uid)}; Path=/; Max-Age=31536000; SameSite=Lax`;
  return uid;
}

export function getOrCreateUid(): string {
  return ensureUid();
}

function apiHeaders(extra?: Record<string, string>): HeadersInit {
  return {
    "Content-Type": "application/json",
    "X-Labler-Uid": ensureUid(),
    ...(extra ?? {}),
  };
}

export async function listTemplates(): Promise<StoredTemplate[]> {
  const r = await fetch(`${API_BASE}/templates`, { headers: { "X-Labler-Uid": ensureUid() } });
  if (!r.ok) throw new Error(await r.text());
  return await r.json();
}

export async function listTemplatesArchived(archived: boolean): Promise<StoredTemplate[]> {
  const r = await fetch(`${API_BASE}/templates?archived=${archived ? "1" : "0"}`, { headers: { "X-Labler-Uid": ensureUid() } });
  if (!r.ok) throw new Error(await r.text());
  return await r.json();
}

export async function saveTemplate(doc: DocV1): Promise<{ id: string }> {
  const r = await fetch(`${API_BASE}/templates`, {
    method: "POST",
    headers: apiHeaders(),
    body: JSON.stringify(doc),
  });
  if (!r.ok) throw new Error(await r.text());
  return await r.json();
}

export async function getTemplate(id: string): Promise<DocV1> {
  const r = await fetch(`${API_BASE}/templates/${encodeURIComponent(id)}`, { headers: { "X-Labler-Uid": ensureUid() } });
  if (!r.ok) throw new Error(await r.text());
  return await r.json();
}

export async function archiveTemplate(id: string): Promise<void> {
  const r = await fetch(`${API_BASE}/templates/${encodeURIComponent(id)}/archive`, { method: "POST", headers: { "X-Labler-Uid": ensureUid() } });
  if (!r.ok) throw new Error(await r.text());
}

export async function unarchiveTemplate(id: string): Promise<void> {
  const r = await fetch(`${API_BASE}/templates/${encodeURIComponent(id)}/unarchive`, { method: "POST", headers: { "X-Labler-Uid": ensureUid() } });
  if (!r.ok) throw new Error(await r.text());
}

